import React from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  To,
} from 'react-router-dom';

import PrivateRoute from '../../framework/containers/PrivateRoute';
import keycloak from '../../framework/utils/KeycloakUtils';
import { pageKeywords, pagePaths } from '../constants/Page';
import {
  Assignment,
  AssignmentOperation,
  AssignmentRedirect,
} from '../containers/pages/assignment';
import AttendanceReport from '../containers/pages/attendance-report/AttendanceReport';
import DailyOperation from '../containers/pages/daily-operation/DailyOperation';
import {
  ExpenseDetail,
  ExpenseDetailCreate,
  ExpenseDetailEdit,
  ExpenseDetailRedirect,
  ExpenseDetailView,
} from '../containers/pages/expense';
import MatterExpense from '../containers/pages/expense/matter/MatterExpense';
import ExpenseReimbursement from '../containers/pages/expense/reimbursement/ExpenceReimbursement';
import Help from '../containers/pages/help/Help';
import MonthlyOperation from '../containers/pages/monthly-operation/MonthlyOperation';
import Notification from '../containers/pages/notification/Notification';
import Invoice from '../containers/pages/outsourcer/invoice/Invoice';
import {
  OutsourcerWorker,
  OutsourcerWorkerCreate,
  OutsourcerWorkerEdit,
  OutsourcerWorkerRedirect,
  OutsourcerWorkerView,
} from '../containers/pages/outsourcer/worker';
import WorkerAssignment from '../containers/pages/outsourcer/worker-assignment/WorkerAssignment';
import OutsourceMember from '../containers/pages/outsourcer/worker-management/WorkerManagement';
import Password from '../containers/pages/password/Password';
import Payslip from '../containers/pages/payslip/Payslip';
import Portal from '../containers/pages/portal/Portal';
import ShiftApplication from '../containers/pages/shift-application/ShiftApplication';
import Top from '../containers/pages/top/Top';
import {
  UserInfo,
  UserInfoEdit,
  UserInfoView,
} from '../containers/pages/user-info';
import {
  AssignWorkInfo,
  WorkContent,
  WorkContentRedirect,
} from '../containers/pages/work-content';
import {
  Report,
  WorkReport,
  WorkReportRedirect,
} from '../containers/pages/work-report';
import NavigateGoBack from './GoBack';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LocationState = { from: To; [x: string]: any };

const AppRouter: React.FunctionComponent = () => {
  // c.f. P291_ENGENE-DDS-013-詳細設計書-SE・外注アプリ.xlsx - 画面・機能一覧シート - パス - 逆引き
  return (
    <Router basename="/worker/">
      <Routes>
        <Route path={pageKeywords.main.ROOT}>
          <Route index element={<Portal />} />
          <Route element={<PrivateRoute keycloak={keycloak} />}>
            <Route path={pageKeywords.main.TOP} element={<Top />} />

            <Route path={pageKeywords.main.USER_INFO} element={<UserInfo />}>
              <Route
                index
                element={
                  <Navigate
                    replace
                    to={`${pagePaths.USER_INFO}/${pageKeywords.mode.VIEW}`}
                  />
                }
              />
              <Route path={pageKeywords.mode.EDIT} element={<UserInfoEdit />} />
              <Route path={pageKeywords.mode.VIEW} element={<UserInfoView />} />
              <Route
                path="*"
                element={
                  <Navigate
                    replace
                    to={`${pagePaths.USER_INFO}/${pageKeywords.mode.VIEW}`}
                  />
                }
              />
            </Route>

            <Route path={pageKeywords.main.PASSWORD} element={<Password />} />

            <Route
              path={pageKeywords.main.DAILY_OPERATION}
              element={<DailyOperation />}
            />

            <Route path={pageKeywords.main.ASSIGNMENT}>
              <Route index element={<NavigateGoBack />} />
              <Route path=":assignMessageId" element={<Assignment />}>
                <Route index element={<AssignmentOperation />} />
                {/* <Route
                  path={pageKeywords.mode.VIEW}
                  element={<AssignmentOperation mode="view" />}
                />
                <Route
                  path={pageKeywords.mode.EDIT}
                  element={<AssignmentOperation mode="edit" />}
                /> */}
              </Route>
            </Route>

            <Route path={pageKeywords.main.WORK_CONTENT}>
              <Route index element={<NavigateGoBack />} />
              <Route path=":date">
                <Route index element={<NavigateGoBack />} />
                <Route path=":usercode" element={<WorkContent />}>
                  <Route index element={<WorkContentRedirect />} />
                  <Route
                    path={pageKeywords.mode.VIEW}
                    element={<AssignWorkInfo mode="view" />}
                  />
                  <Route
                    path={pageKeywords.mode.EDIT}
                    element={<AssignWorkInfo mode="edit" />}
                  />
                </Route>
              </Route>
            </Route>

            <Route path={pageKeywords.main.WORK_REPORT}>
              <Route index element={<NavigateGoBack />} />
              <Route path=":dailyMatterId" element={<WorkReport />}>
                <Route index element={<WorkReportRedirect />} />
                <Route
                  path={pageKeywords.mode.VIEW}
                  element={<Report mode="view" />}
                />
                <Route
                  path={pageKeywords.mode.EDIT}
                  element={<Report mode="edit" />}
                />
              </Route>
            </Route>

            <Route
              path={pageKeywords.main.MONTHLY_OPERATION}
              element={<MonthlyOperation />}
            />

            <Route
              path={pageKeywords.main.SHIFT_APPLICATION}
              element={<ShiftApplication />}
            />

            <Route
              path={pageKeywords.main.ATTENDANCE_REPORT}
              element={<AttendanceReport />}
            />

            <Route path={pageKeywords.main.EXPENSE}>
              <Route index element={<NavigateGoBack />} />
              <Route path={pageKeywords.sub.MATTER}>
                <Route index element={<NavigateGoBack />} />
                <Route path=":dailyMatterId" element={<MatterExpense />} />
              </Route>
              <Route path={pageKeywords.sub.DETAIL} element={<ExpenseDetail />}>
                <Route index element={<ExpenseDetailCreate />} />
                <Route path=":expenseId">
                  <Route index element={<ExpenseDetailRedirect />} />
                  <Route
                    path={pageKeywords.mode.VIEW}
                    element={<ExpenseDetailView />}
                  />
                  <Route
                    path={pageKeywords.mode.EDIT}
                    element={<ExpenseDetailEdit />}
                  />
                </Route>
              </Route>
              <Route
                path={pageKeywords.sub.REIMBURSEMENT}
                element={<ExpenseReimbursement />}
              />
            </Route>

            <Route path={pageKeywords.main.PAYSLIP} element={<Payslip />} />

            <Route path={pageKeywords.main.OUTSOURCER}>
              <Route index element={<NavigateGoBack />} />
              <Route
                path={pageKeywords.sub.WORKER}
                element={<OutsourcerWorker />}
              >
                <Route index element={<OutsourcerWorkerCreate />} />
                <Route path=":outsourcerMemberId">
                  <Route index element={<OutsourcerWorkerRedirect />} />
                  <Route
                    path={pageKeywords.mode.EDIT}
                    element={<OutsourcerWorkerEdit />}
                  />
                  <Route
                    path={pageKeywords.mode.VIEW}
                    element={<OutsourcerWorkerView />}
                  />
                </Route>
              </Route>
              <Route path={pageKeywords.sub.WORKER_ASSIGNMENT}>
                <Route index element={<NavigateGoBack />} />
                <Route path=":dailyMatterId" element={<WorkerAssignment />} />
              </Route>
              <Route path={pageKeywords.sub.INVOICE} element={<Invoice />} />
              <Route
                path={pageKeywords.sub.WORKER_MANAGEMENT}
                element={<OutsourceMember />}
              />
            </Route>

            <Route
              path={pageKeywords.main.NOTIFICATION}
              element={<Notification />}
            />

            <Route path={pageKeywords.main.HELP} element={<Help />} />

            <Route path="*" element={<Navigate replace to={pagePaths.TOP} />} />
          </Route>
        </Route>
      </Routes>
    </Router>
  );
};
export default AppRouter;
